import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { UserProfile } from '@shared/types';
import { queryClient } from '../lib/queryClient';

const REFRESH_DEBOUNCE_MS = 100;

// This refreshes browser presentation. Server routes must enforce current authority.
export function useSessionProfile() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const currentUser = useRef<UserProfile | null>(null);
  const generation = useRef(0);
  const suspended = useRef(false);
  const refreshTimer = useRef<ReturnType<typeof setTimeout>>();

  const cancelScheduledRefresh = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = undefined;
  }, []);

  const suspendRefresh = useCallback(() => {
    ++generation.current;
    suspended.current = true;
    cancelScheduledRefresh();
  }, [cancelScheduledRefresh]);

  const acceptSession = useCallback((next: UserProfile | null, expired = false) => {
    setSessionExpired(expired);
    ++generation.current;
    cancelScheduledRefresh();
    suspended.current = next === null;
    currentUser.current = next;
    if (next === null) {
      // Unmount signed-in query observers before clearing their cache. Navigation
      // can otherwise render the old user and rebuild queries during sign-out.
      flushSync(() => {
        setUser(null);
        setLoading(false);
      });
      queryClient.clear();
      return;
    }
    setUser(next);
    setLoading(false);
  }, [cancelScheduledRefresh]);

  const refresh = useCallback(async () => {
    if (suspended.current) return;
    const request = ++generation.current;
    const expected = currentUser.current;
    try {
      const response = await fetch('/api/auth/me', {
        credentials: 'include',
        redirect: 'error', // Only the requested session endpoint may supply profile data.
      });
      if (request !== generation.current) return;
      if (response.status === 401 || response.status === 403) {
        // Only an observed signed-in session can expire; 403 may be revocation.
        acceptSession(null, response.status === 401 && expected !== null);
        return;
      }
      if (!response.ok) return;
      const data = await response.json();
      if (request !== generation.current) return;
      const next = data.user as UserProfile | undefined;
      // A refresh cannot silently switch identity or tenant. A future explicit
      // server-approved switch must invalidate this generation via acceptSession.
      if (!next?.id || !next.organizationId || (expected &&
          (next.id !== expected.id || next.organizationId !== expected.organizationId))) {
        acceptSession(null);
        return;
      }
      currentUser.current = next;
      setUser(next);
    } catch {
      // Transient failures retain the current display; never log profile data.
      // This is not an authorization cache and grants no server access.
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [acceptSession]);

  useEffect(() => {
    void refresh();
    const onFocus = () => {
      if (suspended.current) return;
      // Invalidate older reads immediately, not after the debounce delay.
      ++generation.current;
      cancelScheduledRefresh();
      refreshTimer.current = setTimeout(() => {
        refreshTimer.current = undefined;
        void refresh();
      }, REFRESH_DEBOUNCE_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      ++generation.current;
      cancelScheduledRefresh();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh, cancelScheduledRefresh]);

  return { user, loading, sessionExpired, acceptSession, suspendRefresh };
}
